import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  Button,
  Card,
  Collapse,
  Divider,
  Empty,
  Popconfirm,
  Space,
  Tag,
  Typography,
  Upload,
  notification,
} from 'antd';
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  FileTextOutlined,
  FileWordOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteCompanyCustomTemplate,
  downloadCompanyCustomTemplate,
  getCustomTemplateTokens,
  uploadCompanyCustomTemplate,
} from '@/services/companies.service';
import {
  listDocumentTemplates,
  archiveDocumentTemplate,
} from '@/services/documentTemplates.service';
import { QUERY_KEY } from '@/shared/constants';
import { useAuthStore } from '@/store/useAuthStore';
import { ROLES } from '@/constants';
import TemplateBuilderModal from './TemplateBuilderModal';
import TokenChips from '@/components/tiptap-editor/TokenChips';

const { Text, Paragraph } = Typography;

const SUPPORTED_DOCUMENT_TYPES = [
  {
    key: 'workFromHome',
    labelKey: 'workFromHome',
    labelFallback: 'Rad od kuće',
  },
  // Extend as more document types gain custom-template support
];

/**
 * Renders one upload slot per supported document type. For each slot the UI
 * shows the current template (filename + uploaded date) and actions:
 * download, replace, delete.
 */
export default function CustomTemplatesForm({
  isEdited,
  companyId,
  customTemplates,
  onChanged,
}) {
  const { t } = useTranslation(['forms', 'messages']);
  const queryClient = useQueryClient();
  const userRoles = useAuthStore((state) => state.user?.roles) || [];
  // Managing global (cross-company) templates is limited to the two highest HLB
  // roles — plain `admin` cannot edit/archive them.
  const canManageGlobal =
    userRoles.includes(ROLES.techAdmin) ||
    userRoles.includes(ROLES.superAdmin);

  const { data: tokensResponse } = useQuery({
    queryKey: [QUERY_KEY.customTemplateTokens],
    queryFn: getCustomTemplateTokens,
    staleTime: Infinity,
  });
  const tokens = tokensResponse?.data || [];

  // --- Builder templates (new DocumentTemplate collection) ---
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  // 'builder' = author HTML in the editor; 'upload' = configure a picked DOCX.
  const [builderMode, setBuilderMode] = useState('builder');
  const [uploadFile, setUploadFile] = useState(null);

  const { data: builderTemplates = [] } = useQuery({
    queryKey: ['documentTemplates', companyId],
    queryFn: () => listDocumentTemplates(companyId),
    enabled: Boolean(companyId),
  });

  const archiveMutation = useMutation({
    mutationFn: (id) => archiveDocumentTemplate(id),
    onSuccess: () => {
      notification.success({
        message: t('success', { ns: 'messages' }),
        placement: 'bottomRight',
      });
      queryClient.invalidateQueries({ queryKey: ['documentTemplates', companyId] });
    },
    onError: (error) => {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description: error?.response?.data?.message || error.message,
        placement: 'bottomRight',
      });
    },
  });

  const openNewBuilder = () => {
    setBuilderMode('builder');
    setUploadFile(null);
    setEditingTemplate(null);
    setBuilderOpen(true);
  };
  const openEditBuilder = (template) => {
    setBuilderMode('builder');
    setUploadFile(null);
    setEditingTemplate(template);
    setBuilderOpen(true);
  };
  // Picking a DOCX opens the same modal in upload mode so the client configures
  // the uploaded contract (name, audience, leave binding, ad-hoc fields) instead
  // of a blind upload.
  const openUploadBuilder = (file) => {
    setBuilderMode('upload');
    setUploadFile(file);
    setEditingTemplate(null);
    setBuilderOpen(true);
  };

  const uploadMutation = useMutation({
    mutationFn: ({ documentType, file }) =>
      uploadCompanyCustomTemplate(companyId, documentType, file),
    onSuccess: (response) => {
      notification.success({
        message: t('success', { ns: 'messages' }),
        description: response?.message,
        placement: 'bottomRight',
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.company, companyId, 'docsSettings'],
      });
      onChanged?.();
    },
    onError: (error) => {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description:
          error?.response?.data?.message || t('forms:templateSaveFailed'),
        placement: 'bottomRight',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (documentType) =>
      deleteCompanyCustomTemplate(companyId, documentType),
    onSuccess: (response) => {
      notification.success({
        message: t('success', { ns: 'messages' }),
        description: response?.message,
        placement: 'bottomRight',
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.company, companyId, 'docsSettings'],
      });
      onChanged?.();
    },
    onError: (error) => {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description:
          error?.response?.data?.message || t('forms:documentDeleteFailed'),
        placement: 'bottomRight',
      });
    },
  });

  const getExistingTemplate = (documentType) => {
    if (!customTemplates) return null;
    // customTemplates may come through as a Map-like object or plain object
    // depending on how Mongoose serializes on this code path.
    if (customTemplates instanceof Map) {
      return customTemplates.get(documentType) || null;
    }
    return customTemplates[documentType] || null;
  };

  return (
    <div className="mt-6">
      <Divider style={{ backgroundColor: '#2db1ab50', margin: '20px 0' }} />
      <div className="mb-3">
        <h4 className="m-0 font-semibold">
          {t('customDocumentTemplates', { ns: 'forms' })}
        </h4>
        <Text type="secondary" className="mt-1 block text-xs">
          {t('customDocumentTemplatesHint', { ns: 'forms' })}
        </Text>
        {isEdited && (
          <div className="mt-3 flex flex-wrap gap-2">
            <Upload
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              showUploadList={false}
              beforeUpload={(file) => {
                openUploadBuilder(file);
                return false;
              }}
            >
              <Button
                className="btn-default btn-default--hover"
                icon={<FileWordOutlined />}
              >
                {t('uploadWordTemplate', {
                  ns: 'forms',
                  defaultValue: 'Otpremi Word šablon',
                })}
              </Button>
            </Upload>
            <Button
              type="primary"
              className="btn-primary"
              icon={<PlusOutlined />}
              onClick={openNewBuilder}
            >
              {t('createTemplate', {
                ns: 'forms',
                defaultValue: 'Napravi šablon',
              })}
            </Button>
          </div>
        )}
      </div>

      {/* Builder templates (odluke/ugovori napravljeni u editoru) */}
      <div className="mb-4">
        {builderTemplates.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('noBuilderTemplates', {
              ns: 'forms',
              defaultValue: 'Nema napravljenih šablona',
            })}
          />
        ) : (
          <div className="flex flex-col gap-2">
            {builderTemplates.map((tpl) => (
              <Card key={tpl._id} size="small" className="border border-gray-200">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Space>
                    {tpl.source === 'upload' ? (
                      <FileWordOutlined />
                    ) : (
                      <FileTextOutlined />
                    )}
                    <span className="font-medium">{tpl.name}</span>
                    <Tag>
                      {t(`templateCategory_${tpl.category}`, {
                        ns: 'forms',
                        defaultValue: tpl.category,
                      })}
                    </Tag>
                    {tpl.source === 'upload' && (
                      <Tag color="blue">Word</Tag>
                    )}
                    {tpl.scope === 'global' && (
                      <Tag color="purple">
                        {t('globalTemplateTag', {
                          ns: 'forms',
                          defaultValue: 'Globalni',
                        })}
                      </Tag>
                    )}
                    {tpl.autoGenerateOnLeaveType && (
                      <Tag color="green">
                        {tpl.autoGenerateOnLeaveType === 'termination'
                          ? t('autoTagTermination', { ns: 'forms' })
                          : t('autoTag', {
                              ns: 'forms',
                              defaultValue: 'Auto na odobrenje',
                            })}
                      </Tag>
                    )}
                    {tpl.emailDelivery && (
                      <Tag color="cyan">
                        {t(`templateEmailDelivery_${tpl.emailDelivery}`, {
                          ns: 'forms',
                          defaultValue: tpl.emailDelivery,
                        })}
                      </Tag>
                    )}
                    {tpl.contractKind && (
                      <Tag color="geekblue">
                        {t(`templateContractKind_${tpl.contractKind}`, {
                          ns: 'forms',
                          defaultValue: tpl.contractKind,
                        })}
                      </Tag>
                    )}
                    {tpl.replacesDefaultDocument && (
                      <Tag color="volcano">
                        {t('replacesDefaultTag', {
                          ns: 'forms',
                          defaultValue: 'Zamenjuje podrazumevani',
                        })}
                      </Tag>
                    )}
                  </Space>
                  {/* Global (HLB-authored) templates are read-only for clients. */}
                  {isEdited && (tpl.scope !== 'global' || canManageGlobal) && (
                    <Space>
                      {/* Builder templates open in the editor; upload templates
                          open in config-edit mode (DOCX stays, config changes). */}
                      <Button
                        size="small"
                        className="!rounded-[var(--buttonRadius)]"
                        icon={<EditOutlined />}
                        onClick={() => openEditBuilder(tpl)}
                      >
                        {tpl.source === 'upload'
                          ? t('editConfig', {
                              ns: 'forms',
                              defaultValue: 'Podešavanja',
                            })
                          : t('edit', { ns: 'forms', defaultValue: 'Izmeni' })}
                      </Button>
                      <Popconfirm
                        title={t('confirmArchiveTemplate', {
                          ns: 'messages',
                          defaultValue: 'Arhivirati šablon?',
                        })}
                        okText={t('yes', { ns: 'forms' })}
                        cancelText={t('no', { ns: 'forms' })}
                        onConfirm={() => archiveMutation.mutate(tpl._id)}
                      >
                        <Button
                          size="small"
                          danger
                          className="!rounded-[var(--buttonRadius)]"
                          icon={<DeleteOutlined />}
                        >
                          {t('archive', { ns: 'forms', defaultValue: 'Arhiviraj' })}
                        </Button>
                      </Popconfirm>
                    </Space>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <TemplateBuilderModal
        open={builderOpen}
        onClose={() => setBuilderOpen(false)}
        companyId={companyId}
        template={editingTemplate}
        mode={builderMode}
        file={uploadFile}
      />

      <Typography.Title level={5} className="mt-2">
        {t('legacyDocxTemplates', {
          ns: 'forms',
          defaultValue: 'Word šablon za rad od kuće',
        })}
      </Typography.Title>
      <Text type="secondary" className="mb-3 block text-xs">
        {t('legacyDocxTemplatesHint', { ns: 'forms' })}
      </Text>

      <div className="flex flex-col gap-3">
        {SUPPORTED_DOCUMENT_TYPES.map(({ key, labelKey, labelFallback }) => {
          const existing = getExistingTemplate(key);
          const isUploading =
            uploadMutation.isPending &&
            uploadMutation.variables?.documentType === key;
          const isDeleting =
            deleteMutation.isPending && deleteMutation.variables === key;

          return (
            <Card
              key={key}
              size="small"
              className="border border-gray-200"
              title={
                <span className="flex items-center gap-2">
                  <FileWordOutlined />
                  {t(labelKey, {
                    ns: 'forms',
                    defaultValue: labelFallback,
                  })}
                </span>
              }
            >
              {existing?.originalName ? (
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <Tag color="blue" icon={<FileWordOutlined />}>
                    {existing.originalName}
                  </Tag>
                  {existing.uploadedAt && (
                    <Text type="secondary" className="text-xs">
                      {dayjs(existing.uploadedAt).format('DD/MM/YYYY HH:mm:ss')}
                    </Text>
                  )}
                  <Button
                    size="small"
                    className="!rounded-[var(--buttonRadius)]"
                    icon={<DownloadOutlined />}
                    onClick={() =>
                      downloadCompanyCustomTemplate(
                        companyId,
                        key,
                        existing.originalName
                      )
                    }
                  >
                    {t('download', { ns: 'forms', defaultValue: 'Download' })}
                  </Button>
                  {isEdited && (
                    <Popconfirm
                      title={t('confirmDeleteTemplate', { ns: 'messages' })}
                      okText={t('yes', { ns: 'forms' })}
                      cancelText={t('no', { ns: 'forms' })}
                      onConfirm={() => deleteMutation.mutate(key)}
                    >
                      <Button
                        size="small"
                        danger
                        className="!rounded-[var(--buttonRadius)]"
                        icon={<DeleteOutlined />}
                        loading={isDeleting}
                      >
                        {t('delete', { ns: 'forms' })}
                      </Button>
                    </Popconfirm>
                  )}
                </div>
              ) : (
                <Text type="secondary" className="mb-2 block text-xs">
                  {t('noCustomTemplate', { ns: 'forms' })}
                </Text>
              )}

              {isEdited && (
                <Upload
                  accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    // Upload immediately on file pick — avoids a confusing
                    // two-step "pick then save" flow.
                    uploadMutation.mutate({ documentType: key, file });
                    return false;
                  }}
                >
                  <Button
                    size="small"
                    className="!rounded-[var(--buttonRadius)]"
                    icon={<UploadOutlined />}
                    loading={isUploading}
                  >
                    {existing
                      ? t('replaceTemplate', { ns: 'forms' })
                      : t('uploadTemplate', { ns: 'forms' })}
                  </Button>
                </Upload>
              )}
            </Card>
          );
        })}
      </div>

      <Collapse
        className="mt-4"
        size="small"
        items={[
          {
            key: 'tokens',
            label: t('availablePlaceholders', { ns: 'forms' }),
            children: (
              <>
                <Paragraph className="text-xs">
                  {t('placeholderInstruction', { ns: 'forms' })}
                </Paragraph>
                {/* Same clickable chips as the builder; click copies the raw
                    {token} to paste into the uploaded Word template. */}
                <TokenChips tokens={tokens} />
              </>
            ),
          },
        ]}
      />
    </div>
  );
}
